// src/marketing/budget/ExpenseChart.tsx
import { useState } from 'react'
import type { DailyExpense } from '../../lib/types'
import { LineChart } from '../../stats/LineChart'
import { expenseSeries, periodTotals, sumBy } from './aggregate'

type Granularity = 'day' | 'week' | 'month'
const GRANULARITY_LABELS: Record<Granularity, string> = {
  day: 'Jour', week: 'Semaine', month: 'Mois',
}

function todayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function ExpenseChart({ expenses }: { expenses: DailyExpense[] }) {
  const [granularity, setGranularity] = useState<Granularity>('day')
  const today = todayIso()
  const series = expenseSeries(expenses, granularity, today)
  const totals = periodTotals(expenses, today)

  // Fenêtre de la courbe = borne du premier point de la série
  const windowStart = series[0]?.date ?? today
  const windowed = expenses.filter((e) => e.expenseDate >= windowStart && e.expenseDate <= today)
  const byCategory = sumBy(windowed, 'category')
  const byPlatform = sumBy(windowed, 'platform')

  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <Counter testid="expense-total-today" label="Aujourd'hui" value={totals.today} />
        <Counter testid="expense-total-7d" label="7 jours" value={totals.last7} />
        <Counter testid="expense-total-month" label="Mois en cours" value={totals.monthToDate} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.375rem' }}>
          {(Object.keys(GRANULARITY_LABELS) as Granularity[]).map((g) => (
            <button key={g} onClick={() => setGranularity(g)}
              style={{
                background: granularity === g ? 'var(--elevated)' : 'transparent',
                border: '1px solid var(--line)', borderRadius: '6px',
                padding: '0.25rem 0.625rem', fontSize: '0.8125rem',
                color: granularity === g ? 'var(--fg)' : 'var(--muted)',
                cursor: 'pointer', fontFamily: 'inherit',
              }}>
              {GRANULARITY_LABELS[g]}
            </button>
          ))}
        </div>
      </div>

      <LineChart points={series} metric="dépense (€)" width={720} height={200} />

      <div style={{ display: 'flex', gap: '2.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
        <TotalsList title="Par catégorie" rows={byCategory} />
        <TotalsList title="Par plateforme" rows={byPlatform} />
      </div>
    </div>
  )
}

function Counter({ testid, label, value }: { testid: string; label: string; value: number }) {
  return (
    <div data-testid={testid}>
      <p style={{ margin: 0, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--muted)' }}>{label}</p>
      <p style={{ margin: '0.125rem 0 0', fontSize: '1.125rem', fontWeight: 700, color: 'var(--fg)' }}>{value.toFixed(2)} €</p>
    </div>
  )
}

function TotalsList({ title, rows }: { title: string; rows: { key: string; total: number }[] }) {
  if (rows.length === 0) return null
  return (
    <div style={{ minWidth: '200px' }}>
      <p style={{ margin: '0 0 0.375rem', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--muted)' }}>{title}</p>
      {rows.map((r) => (
        <div key={r.key} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', fontSize: '0.8125rem', padding: '0.125rem 0' }}>
          <span style={{ color: 'var(--fg)' }}>{r.key}</span>
          <span style={{ color: 'var(--muted)' }}>{r.total.toFixed(2)} €</span>
        </div>
      ))}
    </div>
  )
}
